import React, { FC, useState } from "react";
import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Flex,
  HStack,
  Text,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Link as GatsbyLink } from "gatsby";
import { ShiIcon } from "src/atoms";
import { Link } from "src/atoms/PrismicLink";
import ShiButton from "src/atoms/ShiButton";
import { MobileDetailPanel } from "./MobileDetailPanel";
import { MenuItem, MenuSubItem, MenuData } from "../types";

type Panel =
  | { level: "root" }
  | { level: "sublist"; menuItem: MenuItem }
  | { level: "detail"; menuItem: MenuItem; item: MenuSubItem };

interface MobileNavProps {
  menuData?: MenuData;
  isOpen: boolean;
  onClose: () => void;
}

const hasHeaderFragment = (menuItem: MenuItem) =>
  (menuItem.items || []).some((it) => it.header_fragment?.document?.data);

/**
 * Mobile/tablet drill-down nav. Same behavior both viewports -
 * only the Drawer placement/width differs (right+full on mobile, right+fixed on tablet).
 */
export const MobileNav: FC<MobileNavProps> = ({
  menuData,
  isOpen,
  onClose,
}) => {
  const [stack, setStack] = useState<Panel[]>([{ level: "root" }]);
  const size = useBreakpointValue({ base: "full", sm: "sm" });

  const current = stack[stack.length - 1];

  const pushPanel = (panel: Panel) => setStack((s) => [...s, panel]);
  const popPanel = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));

  const handleClose = () => {
    onClose();
    // Reset to root after the close transition so re-opening doesn't flash the last panel.
    window.setTimeout(() => setStack([{ level: "root" }]), 200);
  };

  const handleMenuItemClick = (menuItem: MenuItem) => {
    const items = menuItem.items || [];

    if (!hasHeaderFragment(menuItem)) {
      if (menuItem.primary?.link?.url) {
        window.location.href = menuItem.primary.link.url;
      }
      return;
    }

    const hasLabeledItems = items.some((it) => it.sub_menu_label);
    const showSubList =
      menuItem.primary?.has_sub_menus && hasLabeledItems && items.length > 1;

    if (showSubList) {
      pushPanel({ level: "sublist", menuItem });
    } else {
      pushPanel({ level: "detail", menuItem, item: items[0] });
    }
  };

  const handleSubItemClick = (menuItem: MenuItem, item: MenuSubItem) => {
    pushPanel({ level: "detail", menuItem, item });
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} placement="right" size={size}>
      <DrawerOverlay />
      <DrawerContent display="flex" flexDirection="column" maxH="100dvh">
        <DrawerHeader
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          paddingY="space-24"
          paddingX="space-24"
        >
          {current.level === "root" ? (
            <GatsbyLink to="/">
              <ShiIcon name="ShineLogo" width="90px" height="20px" />
            </GatsbyLink>
          ) : (
            <Flex
              as="button"
              align="center"
              onClick={popPanel}
              cursor="pointer"
              color="btn-green-bg"
            >
              <ShiIcon
                name="arrow"
                boxSize="14.5px"
                color="btn-green-bg"
              />
              <Text fontSize="font-16" fontWeight="semibold" lineHeight="1.2">
                Back
              </Text>
            </Flex>
          )}

          <DrawerCloseButton
            position="unset"
            size="md"
            color="btn-nav-icon"
            sx={{ svg: { width: "16px", height: "16px", border: "1.5px" } }}
          />
        </DrawerHeader>

        <DrawerBody
          flex="1"
          overflowY="auto"
          position="relative"
          paddingX="space-16"
          paddingBottom="180px"
          paddingTop="0px"
        >
          {current.level === "root" && (
            <VStack align="stretch" spacing="0">
              {(menuData?.body || []).map((menuItem, i: number) => (
                <HStack
                  as="button"
                  key={i}
                  w="full"
                  justifyContent="space-between"
                  py="space-16"
                  borderBottom="1px"
                  borderColor="monochrome-200"
                  _last={{ borderBottom: "none" }}
                  onClick={() => handleMenuItemClick(menuItem)}
                >
                  <Text
                    fontSize="font-18"
                    fontWeight="semibold"
                    lineHeight="1.2"
                    color="monochrome-900"
                  >
                    {menuItem.primary?.menu_title}
                  </Text>
                  {hasHeaderFragment(menuItem) && (
                    <ShiIcon
                      name="arrow"
                      boxSize="14.5px"
                      transform="rotate(180deg)"
                      color="btn-nav-icon"
                    />
                  )}
                </HStack>
              ))}
            </VStack>
          )}

          {current.level === "sublist" && (
            <VStack align="stretch" spacing="0">
              {(current.menuItem.items || []).map((item, i: number) => (
                <HStack
                  as="button"
                  key={i}
                  w="full"
                  justifyContent="space-between"
                  py="space-16"
                  borderBottom="1px"
                  borderColor="monochrome-200"
                  _last={{ borderBottom: "none" }}
                  onClick={() => handleSubItemClick(current.menuItem, item)}
                >
                  <Text
                    fontSize="font-18"
                    fontWeight="semibold"
                    lineHeight="1.2"
                    color="monochrome-900"
                  >
                    {item.sub_menu_label}
                  </Text>
                  <ShiIcon
                    name="arrow"
                    boxSize="14px"
                    transform="rotate(180deg)"
                    color="btn-nav-icon"
                  />
                </HStack>
              ))}
            </VStack>
          )}

          {current.level === "detail" && (
            <MobileDetailPanel item={current.item} />
          )}
        </DrawerBody>

        <DrawerFooter
          position="sticky"
          bottom="0"
          bg="white"
          zIndex="1"
          flexDirection="column"
          boxShadow="0 -4px 12px rgba(0, 0, 0, 0.05)"
        >
          <VStack spacing="space-12" w="full">
            {menuData?.start_cta_label && (
              <ShiButton
                as="a"
                href={menuData.start_cta_link?.url}
                variant="signup"
                w="full"
              >
                {menuData.start_cta_label}
              </ShiButton>
            )}
            {menuData?.login_cta_label && (
              <Link
                href={menuData.login_cta_link?.url}
                variant="link-nav"
                fontWeight="medium"
              >
                {menuData.login_cta_label}
              </Link>
            )}
          </VStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileNav;
